import {
  IAdvisorComparisonTableBlock,
  IAdvisorPackageCardBlock,
  IAdvisorPackageLineItem,
} from "@types";

import { Badge, SENTIMENT_BADGE_VARIANT } from "../Badge";
import { cn } from "../cn";
import { SERIES, seriesColor } from "./chartCommon";
import { numericColumns, parseAmount } from "./tableUtils";

/**
 * S4_RECOMMEND's package card and comparison tables, restyled to match the visual language the
 * other funnel nodes already use — S1A's per-component colour markers, S1B's row markers and
 * card chrome. Every value printed is still exactly what the backend sent; parsing a cell back
 * into a number is done only to size a bar, never to reformat or replace the printed text.
 */

const DISCRETIONARY_LABEL = "Additional bonus (discretionary)";

// The three funded components that build up to the target total cash, mapped to the same
// SERIES index the bar and the line-item dots both read from — one source, so they can't drift.
const COMPONENT_SERIES: Record<string, number> = {
  "Annual Base": 0,
  "Cash Allowances": 1,
  "Target Bonus": 2,
};

/**
 * The base/allowances/bonus/discretionary build-up to target total cash, as proportional
 * segments. Discretionary gets a hatch instead of a solid fill — it's cash no configured plan
 * actually funds, and the bar should say that before the callout explains it.
 */
function CompositionBar({ items }: { items: IAdvisorPackageLineItem[] }) {
  const segments = [...Object.keys(COMPONENT_SERIES), DISCRETIONARY_LABEL]
    .map(label => items.find(item => item.label === label))
    .filter((item): item is IAdvisorPackageLineItem => Boolean(item))
    .map(item => ({ label: item.label, amount: parseAmount(item.value) ?? 0 }))
    .filter(item => item.amount > 0);

  const total = segments.reduce((sum, segment) => sum + segment.amount, 0);
  if (total <= 0) return null;

  return (
    <div className="border-b border-gray-200 px-4 pb-3 pt-3.5">
      <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-gray-100">
        {segments.map(segment => {
          const isDiscretionary = segment.label === DISCRETIONARY_LABEL;
          const seriesIndex = COMPONENT_SERIES[segment.label];
          return (
            <div
              key={segment.label}
              title={segment.label}
              style={{
                width: `${(segment.amount / total) * 100}%`,
                backgroundColor: isDiscretionary
                  ? undefined
                  : SERIES[seriesIndex].stroke,
                backgroundImage: isDiscretionary
                  ? "repeating-linear-gradient(135deg, #f59e0b 0 4px, #fde68a 4px 8px)"
                  : undefined,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

function LineRow({
  item,
  isFirst,
}: {
  item: IAdvisorPackageLineItem;
  isFirst: boolean;
}) {
  const seriesIndex = COMPONENT_SERIES[item.label];
  const isDiscretionary = item.label === DISCRETIONARY_LABEL;
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-4 px-4 py-2.5",
        !isFirst && "border-t border-gray-200",
        item.emphasis && "bg-gray-100"
      )}
    >
      <span
        className={cn(
          "flex items-center gap-2.5 text-sm",
          item.emphasis ? "font-semibold text-gray-800" : "text-gray-500"
        )}
      >
        {seriesIndex !== undefined && (
          <span
            className={cn(
              "h-2.5 w-2.5 shrink-0 rounded-sm",
              SERIES[seriesIndex].dot
            )}
          />
        )}
        {/* Hollow, not filled — the same "not solid yet" cue the bar's hatch gives. */}
        {isDiscretionary && (
          <span className="h-2.5 w-2.5 shrink-0 rounded-sm border-2 border-amber-500" />
        )}
        {item.label}
      </span>
      <span
        className={cn(
          "text-sm",
          item.emphasis ? "font-semibold text-gray-800" : "text-gray-800"
        )}
      >
        {item.value}
      </span>
    </div>
  );
}

/**
 * S4's package card: the same line items as before, plus a composition bar and colour-coded
 * markers on the components that build up to it. Subtotal/total rows (TGC, TTC, TDC) stay
 * undotted — they're sums, not components — and keep the existing bold/tinted emphasis.
 */
export function PackageCompositionCard({
  block,
}: {
  block: IAdvisorPackageCardBlock;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      <CompositionBar items={block.items} />
      {block.items.map((item, i) => (
        <LineRow key={item.label} item={item} isFirst={i === 0} />
      ))}
    </div>
  );
}

/**
 * S4's "How it compares" table: a row marker per metric (matching the bonus breakdown table on
 * the same screen), "Proposed vs Benchmark" collapsed into one cell instead of two separate
 * columns, and the delta as a sentiment-coloured pill — the one number that actually carries
 * the verdict.
 */
export function S4ComparisonsTable({
  block,
}: {
  block: IAdvisorComparisonTableBlock;
}) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-100">
            <th className="whitespace-nowrap px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              Metric
            </th>
            <th className="whitespace-nowrap px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
              Proposed vs benchmark
            </th>
            <th className="whitespace-nowrap px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-gray-500">
              Delta
            </th>
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, i) => {
            const [label, proposed, benchmark, delta] = row.cells;
            return (
              <tr key={i} className="border-b border-gray-200 last:border-b-0">
                <td className="whitespace-nowrap px-4 py-2.5 font-medium text-gray-800">
                  <span className="flex items-center gap-2.5">
                    <span
                      className={cn(
                        "h-2.5 w-2.5 shrink-0 rounded-sm",
                        seriesColor(i).dot
                      )}
                    />
                    {label}
                  </span>
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-gray-500">
                  {proposed} <span className="text-gray-400">vs</span>{" "}
                  {benchmark}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <Badge
                    variant={
                      SENTIMENT_BADGE_VARIANT[row.sentiment ?? "neutral"]
                    }
                  >
                    {delta}
                  </Badge>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * S4's bonus breakdown — the same rows as the generic table, with a row marker per component
 * (mirroring S1B's per-row treatment) and an amber left accent on the discretionary/unfunded
 * row, echoing the composition bar's hatch.
 */
export function BonusBreakdownTable({
  block,
}: {
  block: IAdvisorComparisonTableBlock;
}) {
  const numeric = numericColumns(block);

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-100">
            {block.columns.map((column, i) => (
              <th
                key={i}
                className={cn(
                  "whitespace-nowrap px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-gray-500",
                  i !== 0 && numeric[i] ? "text-right" : "text-left"
                )}
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, r) => {
            const discretionary = row.sentiment === "warning";
            return (
              <tr
                key={r}
                className={cn(
                  "border-b border-gray-200 last:border-b-0",
                  discretionary && "bg-amber-50 bg-opacity-60"
                )}
              >
                {row.cells.map((cell, c) => (
                  <td
                    key={c}
                    className={cn(
                      "whitespace-nowrap px-4 py-2.5 text-gray-800",
                      c === 0 && "font-medium",
                      c === 0 &&
                        discretionary &&
                        "border-l-2 border-l-amber-400",
                      numeric[c] ? "text-right" : "text-left",
                      c > 0 && !numeric[c] && "text-gray-500"
                    )}
                  >
                    {c === 0 ? (
                      <span className="flex items-center gap-2.5">
                        <span
                          className={cn(
                            "h-2.5 w-2.5 shrink-0 rounded-sm",
                            seriesColor(r).dot
                          )}
                        />
                        {cell}
                      </span>
                    ) : (
                      cell
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
